import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { isServerErrorSilenceActive } from './server-error-silence';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private alertSubject = new Subject<any>();
  private note_render = new Subject<boolean>();
  public image_update = new Subject<boolean>();
  private mfaErrorHandler = new Subject<any>();
  private openNotesPanelSubject = new Subject<{ projectId: string | null; leadName: string | null; leadEmail: string | null }>();

  latestNotes$ = this.note_render.asObservable();

  /**
   * Emits when something (e.g. a lead screen) asks the header notes panel to open.
   * Payload carries an optional project id to scope notes to, plus an optional lead
   * first name and email used to personalise / address the "Copy notes" email.
   */
  openNotesPanel$ = this.openNotesPanelSubject.asObservable();

  /**
   * Ask the header notes panel to open, optionally scoped to a project id.
   * @param projectId optional project id to scope the notes request to
   * @param leadName optional lead first name used in the copied-notes email greeting
   * @param leadEmail optional lead email used as the recipient of the copied-notes email
   */
  openNotesPanel(projectId?: string | null, leadName?: string | null, leadEmail?: string | null): void {
    this.openNotesPanelSubject.next({ projectId: projectId ?? null, leadName: leadName ?? null, leadEmail: leadEmail ?? null });
  }

  /**
   * Get the observable stream of alerts
   */
  getAlerts(): Observable<any> {
    return this.alertSubject.asObservable();
  }

  /**
   * Get the observable stream of notes
   */
  getNotes(): Observable<any> {
    return this.note_render.asObservable();
  }

  /**
   * Get the observable stream of MFA errors
   */
  getMfaError(): Observable<any> {
    return this.mfaErrorHandler.asObservable();
  }

  /**
   * Emit a success message to the alert stream
   * @param message the message to emit
   */
  success(message: string): void {
    this.alertSubject.next({ type: 'success', message });
  }

  /**
   * Emit an error message to the alert stream. Dropped while a 5xx is being delivered — 5xx are
   * never shown (see server-error-silence.ts).
   * @param message the message to emit
   */
  error(message: string): void {
    if (isServerErrorSilenceActive()) {
      return;
    }
    this.alertSubject.next({ type: 'error', message });
  }

  /**
   * Emit a MFA error to the error stream
   * @param param the MFA error to emit
   */
  mfaError(param: any): void {
    this.mfaErrorHandler.next(param);
  }

  /**
   * Emit a boolean value to the notes stream
   * @param param the boolean value to emit
   */
  noteAddedUpdate(param: boolean): void {
    this.note_render.next(param);
  }
}
